import React, { FC } from 'react';
import { View } from 'react-native';
import Svg from 'react-native-svg';
import type { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import AnnotationShape from '@v2/components/imageHandling/components/DrawingCanvas/components/AnnotationShape';
import { useManageAnnotationsOverlay } from './hooks/useManageAnnotationsOverlay';

interface AnnotationsOverlayProps {
  image: ImageDocumentStoreItem;
  pageNumber: number;
}

/**
 * Read-only overlay that renders saved annotations on top of the image in view mode.
 * Uses the same letterbox+rotate math as DrawingCanvas so annotations line up pixel-accurately
 * with the image regardless of container size, aspect mismatch, or rotation.
 */
const AnnotationsOverlay: FC<AnnotationsOverlayProps> = ({ image, pageNumber }) => {
  const { layout, handleLayout, rebased, hasAnnotations, viewTransform, documentRotation } = useManageAnnotationsOverlay({ image, pageNumber });

  if (!hasAnnotations) return null;

  const width = layout.viewportWidth || 1;
  const height = layout.viewportHeight || 1;

  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} onLayout={handleLayout} pointerEvents="none">
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          transform: [{ translateX: viewTransform.panX }, { translateY: viewTransform.panY }, { scale: viewTransform.zoom }],
        }}
        pointerEvents="none"
      >
        <Svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
          {rebased.map((a) => (
            <AnnotationShape
              key={a.id}
              annotation={a}
              isSelected={false}
              isEraserHover={false}
              isHovered={false}
              isEditing={false}
              documentRotation={documentRotation}
            />
          ))}
        </Svg>
      </View>
    </View>
  );
};

export default AnnotationsOverlay;
